import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "stukjes_install_dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    // An install event may have been captured globally before mount
    if (window.__deferredInstall) {
      setDeferred(window.__deferredInstall);
      setShow(true);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      window.__deferredInstall = e;
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari has no beforeinstallprompt → show manual hint after a short delay
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => { setIos(true); setShow(true); }, 1500);
    }

    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:left-auto sm:right-4 sm:max-w-sm" data-testid="install-prompt">
      <div className="rounded-2xl border border-blue-200 bg-white p-4 shadow-2xl shadow-blue-900/10 ring-1 ring-black/5">
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="Stukjes" className="h-11 w-11 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-semibold text-slate-900">{t("install.title")}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
              {ios ? (
                <span className="inline-flex flex-wrap items-center gap-1">{t("install.iosHint", { defaultValue: "Tap Share then Add to Home Screen." })} <Share className="inline h-3.5 w-3.5 text-blue-600" /></span>
              ) : t("install.body")}
            </p>
            {!ios && (
              <div className="mt-3 flex items-center gap-2">
                <Button onClick={install} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="install-accept-btn">
                  <Download className="mr-1.5 h-4 w-4" /> {t("install.install")}
                </Button>
                <Button onClick={dismiss} size="sm" variant="ghost" className="text-slate-500" data-testid="install-later-btn">
                  {t("install.later")}
                </Button>
              </div>
            )}
          </div>
          <button onClick={dismiss} data-testid="install-close-btn" className="shrink-0 text-slate-300 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
